import { PHONE_DISPLAY, PHONE_TEL, TID } from "@/config/site";
import { Phone, ArrowRight, ShieldCheck, Clock3, MapPin, Sparkles } from "lucide-react";
import ShareLocationButton from "@/components/site/ShareLocationButton";
import HeroQuoteForm from "@/components/site/HeroQuoteForm";
import { useReveal } from "@/hooks/useReveal";
import { useEffect, useRef, useState } from "react";

const HERO_SLIDES = [
  {
    src: "/images/hero-porsche.webp",
    alt: "BM Towing flatbed loading a Porsche at the Bellevue dealership",
  },
  {
    src: "/images/hero-porsche-2.webp",
    alt: "BM Towing branded flatbed with a Porsche on the deck",
  },
];

/**
 * Luxury hero — real BM Towing photo (Porsche + Bellevue dealership at night)
 * instead of the 3D scene. Adds cinematic zoom/pan Ken Burns motion, a subtle
 * mouse parallax on desktop, and honors prefers-reduced-motion.
 */
function HeroPhoto({ className = "" }) {
  const wrapRef = useRef(null);
  const [i, setI] = useState(0);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(m.matches);
    on();
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setI((v) => (v + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(id);
  }, [reduced]);

  const onMove = (e) => {
    if (reduced) return;
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    setParallax({ x, y });
  };
  const onLeave = () => setParallax({ x: 0, y: 0 });

  return (
    <div
      ref={wrapRef}
      className={`relative overflow-hidden ${className}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="hero-photo"
    >
      {HERO_SLIDES.map((s, idx) => (
        <div
          key={s.src}
          className="absolute inset-0 transition-opacity duration-[1400ms]"
          style={{
            opacity: idx === i ? 1 : 0,
          }}
          aria-hidden={idx !== i}
        >
          <img
            src={s.src}
            alt={s.alt}
            fetchPriority={idx === 0 ? "high" : "auto"}
            decoding="async"
            width="1600"
            height="900"
            loading={idx === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 h-full w-full object-cover ${reduced ? "" : "hero-kenburns"}`}
            style={{
              transform: reduced
                ? "none"
                : `translate3d(${parallax.x * -8}px, ${parallax.y * -6}px, 0) scale(1.06)`,
              filter: "brightness(1.05) contrast(1.06) saturate(1.08)",
              transition: "transform 900ms cubic-bezier(.16,1,.3,1), filter 400ms ease",
            }}
          />
        </div>
      ))}
      {/* Gradient overlays for legibility */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.85) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0) 100%)",
        }}
      />
      {/* Corner brackets */}
      <div className="pointer-events-none absolute inset-3 border border-white/5" />
      <div className="pointer-events-none absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-yellow-400/80" />
      <div className="pointer-events-none absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-yellow-400/80" />
      <div className="pointer-events-none absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-yellow-400/80" />
      <div className="pointer-events-none absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-yellow-400/80" />
      {/* Slide indicator */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        {HERO_SLIDES.map((slide, idx) => (
          <button
            key={`dot-${slide.src}`}
            type="button"
            onClick={() => setI(idx)}
            aria-label={`Show hero slide ${idx + 1}`}
            aria-current={idx === i}
            className={`h-1.5 rounded-full transition-all ${
              idx === i ? "w-8 bg-yellow-400" : "w-3 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Hero() {
  const heroRef = useReveal(0.02);
  const formRef = useReveal(0.1);

  return (
    <section
      id="home"
      className="relative isolate overflow-hidden lux-spotlight border-b border-yellow-400/30"
    >
      <style>{`
        @keyframes heroKenburns {
          0% { transform: scale(1.06) translate3d(0, 0, 0); }
          100% { transform: scale(1.14) translate3d(-1.5%, -1%, 0); }
        }
        .hero-kenburns {
          animation: heroKenburns 18s ease-in-out infinite alternate;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-kenburns { animation: none; }
        }
      `}</style>

      {/* Ambient orbits + noise */}
      <div className="pointer-events-none absolute inset-0 bm-grain" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 60%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-32 w-[560px] h-[560px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(59,130,246,0.35) 0%, rgba(0,0,0,0) 60%)" }}
      />

      <div className="pointer-events-none absolute inset-x-0 top-24 h-px bg-white/5" />
      <div className="pointer-events-none absolute inset-x-0 bottom-24 h-px bg-white/5" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT: cinematic photo stage */}
        <div ref={heroRef} className="lux-reveal lg:col-span-7 order-2 lg:order-1">
          <div className="relative aspect-[16/11] sm:aspect-[16/10] rounded-2xl overflow-hidden lux-glass-strong">
            <HeroPhoto className="absolute inset-0 h-full w-full" />
            <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5 flex items-end justify-between gap-3 z-10">
              <div className="text-white/95">
                <div className="font-display uppercase tracking-[0.28em] text-[10px] sm:text-xs text-yellow-400 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  Real BM Towing Job · Bellevue Porsche
                </div>
                <div className="font-display font-black uppercase text-white text-lg sm:text-2xl leading-tight mt-1">
                  Dispatched. Every day. All night.
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2 lux-glass px-3 py-2 rounded-full">
                <span className="relative inline-block h-2.5 w-2.5 rounded-full bg-green-500 lux-live-dot" />
                <span className="font-display uppercase tracking-widest text-[10px] text-white">Live · 24/7</span>
              </div>
            </div>
          </div>

          {/* Trust chips */}
          <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { icon: Clock3, title: "24/7 Response", desc: "Nights · weekends · holidays" },
              { icon: ShieldCheck, title: "Licensed & Insured", desc: "Trained flatbed operators" },
              { icon: MapPin, title: "Local to Bellevue", desc: "Bellevue · Kirkland · Eastside" },
            ].map((t) => (
              <li
                key={t.title}
                className="lux-glass rounded-xl p-3 sm:p-4 flex items-start gap-3"
                data-testid={`hero-trust-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <t.icon className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-display font-black uppercase text-white text-xs sm:text-sm">{t.title}</div>
                  <div className="text-zinc-400 text-[11px] sm:text-xs leading-snug">{t.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT: eyebrow + headline + CTAs + form */}
        <div ref={formRef} className="lux-reveal lux-reveal-delay-2 lg:col-span-5 order-1 lg:order-2">
          <div className="inline-flex items-center gap-2 lux-glass rounded-full px-3 py-1.5">
            <Phone className="h-3.5 w-3.5 text-yellow-400" strokeWidth={3} aria-hidden="true" />
            <span className="font-display uppercase tracking-[0.25em] text-[10px] text-white/90">
              Call 24 Hours a Day
            </span>
          </div>

          <div className="mt-4 font-display uppercase tracking-[0.32em] text-yellow-400 text-xs">
            Bellevue's 24/7 Tow Truck
          </div>

          <h1 className="mt-3 font-display font-black uppercase text-white leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            Towing Service in <span className="text-yellow-400">Bellevue, WA</span>
            <span className="block">— Premium 24/7 Tow &amp; Recovery</span>
          </h1>

          <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed font-light max-w-xl">
            Local, licensed towing in Bellevue, WA — 24/7 flatbed tow truck service, roadside assistance, jump starts, lockouts, tire changes, and vehicle transport. Also serving Kirkland, Redmond, Bothell, Woodinville, Sammamish, Issaquah, Mercer Island, and Newcastle.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid={TID.heroCall}
              aria-label={`Call BM Towing & Recovery 24/7 at ${PHONE_DISPLAY}`}
              className="group inline-flex items-center justify-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-4 text-base rounded-full lux-glow-yellow transition-colors min-h-[56px]"
            >
              <Phone className="h-5 w-5" strokeWidth={3} aria-hidden="true" />
              Call Now: {PHONE_DISPLAY}
            </a>
            <ShareLocationButton
              testId={TID.heroShareLocationBtn}
              variant="luxury"
              label="Share My Location"
              className="text-base px-6 py-4 min-h-[56px]"
            />
            <a
              href="#contact"
              data-testid={TID.heroQuote}
              aria-label="Request service — scroll to contact form"
              className="inline-flex items-center justify-center gap-2 lux-glass hover:border-yellow-400/50 text-white font-display font-bold uppercase tracking-wider px-6 py-4 text-sm rounded-full transition-colors min-h-[56px]"
            >
              Request Service
              <ArrowRight className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
            </a>
          </div>

          <p className="mt-5 font-display uppercase tracking-[0.22em] text-[11px] sm:text-xs text-zinc-500 leading-relaxed">
            Fast response. Safe transport. <span className="text-yellow-400">Trusted 24/7 towing in Bellevue and across the Eastside.</span>
          </p>

          <div className="mt-8">
            <HeroQuoteForm />
          </div>
        </div>
      </div>
    </section>
  );
}
